import React, { useRef, useEffect, forwardRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { ButtonBase } from '@material-ui/core';
import moment from 'moment';
import { DeleteIcon, EditIcon } from '../../../../components/Icon';
import { classNames, initValue } from '../../../../utils';
import { i18n } from '../../../../language';
const useStyles = makeStyles({
  root: {
    // minHeight: '22rem',
    overflow: 'auto',
    padding: '0 1.875rem',
    backgroundColor: '#fff',
  },
  tableBody: {},
  table: {
    position: 'relative',
    minWidth: '35rem',
    '& .MuiTableCell-root': {
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '1.675rem',
      color: '#2e4469',
      padding: '0.813rem 1.25rem',
    },
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: '0',
      left: '0',
      zIndex: '10',
      '& > .MuiTableRow-root': {
        backgroundColor: '#fff',
      },
      '& .MuiTableCell-root': {
        fontWeight: 'bold',
        color: '#538fff',
        backgroundColor: '#fff',
        textTransform: 'uppercase',
        paddingTop: '0px',
      },
    },
    '& .MuiTableBody-root': {
      '& > :hover': {
        backgroundColor: '#f5faff !important',
      },
      '& > .MuiTableRow-root': {
        borderBottom: '1px solid #E6E8E9',
      },
      '& .MuiTableCell-root': {
        height: '3.5rem',
        fontFamily: 'NotoSans-Medium',
      },
    },
  },
  status: {
    width: '0.625rem',
    height: '0.625rem',
    display: 'inline-block',
    backgroundColor: '#ff655c',
    borderRadius: '50%',
    marginRight: '0.563rem',
  },
  editBtn: {
    width: '2.25rem',
    height: '2.25rem',
    minWidth: '0',
    backgroundColor: '#4c93ff',
    borderRadius: '0.625rem',
    color: '#fff',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
  editIcon: {
    fontSize: '1rem',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
  tableHead: {
    whiteSpace: 'nowrap',
  },
  editDisabled: {
    background: '#C1C6CA',
  },
});

/**
 * @param allData => [] => {} =>
 * @param aligns => [] => string
 * @param tableHeads => [] => {} => text, value
 * @returns
 */
export default forwardRef(function Base(
  {
    className,
    allData,
    aligns,
    tableHeads,
    editClick,
    nextPageCall,
    setTableRef,
    pageSize,
    hideIndex,
    setHideIndex,
    editDisabledFunc,
    deleteClick,
    deleteDisabledFunc,
  },
  ref
) {
  const classes = useStyles();
  let isLoading = false;
  const PAGESIZE = pageSize ?? 1;
  const editHandle = (item, index) => {
    return () => {
      if (typeof editClick === 'function') {
        editClick(item, index);
      }
    };
  };

  const tabelCellCtr = (type, value) => {
    if (type === 'date') {
      return moment(value).isValid() ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '';
    } else if (type === 'userStatus') {
      return value === 1 ? 'Active' : 'InActive';
    } else if (type === 'type') {
      return value === 1 ? i18n.t('SETTING_SRS_UPDATE') : i18n.t('SETTING_MANUAL');
    } else if (type === 'flag') {
      return value === 1 ? 'Yes' : 'No';
    }
  };

  let isTouchArea = false;
  const scrollListener = () => {
    const { current } = containerRef;
    if (current.scrollHeight - current.scrollTop <= current.clientHeight + 2) {
      if (!isTouchArea) {
        isTouchArea = true;
        const pageNo = parseInt(allData.length / PAGESIZE + 1);
        nextPageCall(PAGESIZE, pageNo);
      }
    } else {
      isTouchArea = false;
    }
  };

  const tempRef = useRef(null);
  const containerRef = initValue(ref, tempRef);
  useEffect(() => {
    if (typeof setTableRef === 'function') {
      setTableRef(containerRef);
    }
  }, [containerRef]);

  const initRefresh = async () => {
    const elem = containerRef.current.querySelector('.MuiTableBody-root');
    const bottom = 100;
    if (elem.scrollHeight < containerRef.current.clientHeight + bottom && !isLoading) {
      isLoading = true;
      const pageNo = parseInt(allData.length / PAGESIZE + 1);
      await nextPageCall(PAGESIZE, pageNo);
      isLoading = false;
    }
  };

  useEffect(() => {
    if (nextPageCall) {
      const handleResize = () => {
        (async () => {
          await initRefresh();
        })();
      };
      const current = containerRef.current;
      const nextPageCtr = () => {
        current.addEventListener('scroll', scrollListener);
        window.addEventListener('resize', handleResize);
      };
      nextPageCtr();
      (async () => {
        await initRefresh();
      })();
      return () => {
        current.removeEventListener('scroll', scrollListener);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [allData]);

  useEffect(() => {
    let timeId = null;
    let elem = null;
    if (hideIndex !== null && hideIndex !== undefined && hideIndex > -1) {
      elem = containerRef.current.querySelector('.MuiTableBody-root').children[hideIndex];
      if (elem) {
        elem.style.backgroundColor = '#f5faff';
        timeId = setTimeout(() => {
          const elem = containerRef.current.querySelector('.MuiTableBody-root').children[hideIndex];
          elem.style.backgroundColor = '#fff';
          if (typeof setHideIndex === 'function') {
            setHideIndex(null);
          }
        }, 3000);
      }
    }
    return () => {
      timeId && clearTimeout(timeId);
      elem && (elem.style.backgroundColor = '#fff');
    };
  }, [hideIndex]);

  const [preTime, setPreTime] = useState(null);
  const [preIndex, setPreIndex] = useState(-1);
  const doubleClick = (item, index) => {
    return () => {
      if (preIndex === index && moment().diff(preTime, 'milliseconds') < 500) {
        editHandle(item, index)();
      }
      setPreIndex(index);
      setPreTime(moment());
    };
  };

  return (
    <TableContainer className={classNames(className, classes.root)} ref={containerRef}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead} id="setting_table_head">
          <TableRow>
            {tableHeads?.map((item, index) => {
              return (
                <TableCell align={aligns[index]} key={item.text}>
                  {item.text}
                </TableCell>
              );
            })}
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody} id="setting_table_body">
          {Array.isArray(allData) &&
            allData.map((itemlist, itenIndex) => {
              return (
                <TableRow
                  id={`setting_table_row${itenIndex}`}
                  key={JSON.stringify(itemlist)}
                  onClick={editDisabledFunc?.(itemlist, itenIndex) ? null : doubleClick(itemlist, itenIndex)}
                >
                  {tableHeads.map((item, index) => {
                    return (
                      <TableCell
                        key={JSON.stringify(item)}
                        align={aligns[index]}
                        style={{ maxWidth: '25rem', wordWrap: 'break-word', wordBreak: 'break-all' }}
                      >
                        {item.type ? tabelCellCtr(item.type, itemlist[item.value]) : itemlist[item.value]}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <ButtonBase
                      id={`setting_table_edit${itenIndex}`}
                      disabled={editDisabledFunc?.(itemlist, itenIndex) ?? false}
                      className={
                        editDisabledFunc?.(itemlist, itenIndex)
                          ? classNames(classes.editBtn, classes.editDisabled)
                          : classes.editBtn
                      }
                      onClick={editHandle(itemlist, itenIndex)}
                    >
                      <EditIcon className={classes.editIcon} />
                    </ButtonBase>
                    {deleteClick && (
                      <ButtonBase
                        id={`setting_table_edit${itenIndex}`}
                        disabled={deleteDisabledFunc?.(itemlist, itenIndex) ?? false}
                        className={
                          deleteDisabledFunc?.(itemlist, itenIndex)
                            ? classNames(classes.editBtn, classes.editDisabled)
                            : classes.editBtn
                        }
                        onClick={() => deleteClick(itemlist, itenIndex)}
                        style={{ marginLeft: '1em' }}
                      >
                        <DeleteIcon className={classes.editIcon} />
                      </ButtonBase>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
